import React, { useState, useCallback, useRef, useEffect } from 'react';
import styles from './Playhead.module.css';

interface PlayheadProps {
  position: number;
  /** Beat width in pixels (for drag-to-beat conversion) */
  beatWidth?: number;
  /** Current zoom level */
  zoom?: number;
  /** Total beats in timeline */
  totalBeats?: number;
  /** Called when user drags playhead to a new position (GRE-115) */
  onSeek?: (beat: number) => void;
}

export const Playhead: React.FC<PlayheadProps> = ({
  position,
  beatWidth = 60,
  zoom = 1,
  totalBeats = 16,
  onSeek,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const playheadRef = useRef<HTMLDivElement>(null);

  // Handle drag start
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!onSeek) return;
    e.preventDefault();
    setIsDragging(true);
    setDragOffset(e.clientX - position);
  }, [onSeek, position]);

  // Handle drag move and end
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!onSeek) return;

      const newPosition = e.clientX - dragOffset;
      const effectiveBeatWidth = beatWidth * zoom;
      const beat = newPosition / effectiveBeatWidth;
      const clampedBeat = Math.max(0, Math.min(beat, totalBeats));

      onSeek(clampedBeat);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onSeek, beatWidth, zoom, totalBeats]);

  return (
    <div
      ref={playheadRef}
      className={`${styles.playhead} ${isDragging ? styles.dragging : ''}`}
      style={{
        left: `${position}px`,
        cursor: onSeek ? (isDragging ? 'grabbing' : 'grab') : 'default',
      }}
      onMouseDown={handleMouseDown}
      role="slider"
      aria-label="Playhead - drag to seek"
      aria-valuenow={position}
      tabIndex={onSeek ? 0 : -1}
    />
  );
};
